import uniq from 'lodash/uniq'
import plur from 'plur'
import React, {useRef} from 'react'

import {useJobId} from '../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {
  getDraftJobs,
  getOriginalPipelineSettings,
  isPipelineFormOpened,
} from '../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {
  collapsedBlocks,
  pipelineForm,
} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {FormType} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices.types'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import SettingsBlock from '../../../../SettingsBlock/SettingsBlock'
import {AddIntegration} from '../../Integrations/AddIntegration/AddIntegration'
import Integration from '../../Integrations/Integration/Integration'
import {integrations, IntegrationType} from '../../Integrations/Integrations.consts'
import {useAvailableIntegrations} from '../../Integrations/Integrations.hooks'
import IntegrationSettingsForm from '../../Integrations/IntegrationSettingsForm/IntegrationSettingsForm'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {notNull} from 'src/utils/guards'

function JobIntegrations() {
  const jobId = useJobId()!
  const pipelineId = usePipelineId()

  const availableIntegrations = useAvailableIntegrations()
  const savedIntegrations =
    useAppSelector(getOriginalPipelineSettings(pipelineId))?.jobs?.[jobId]?.integrations ?? []
  const usedIntegrations =
    useAppSelector(state => getDraftJobs(state, pipelineId)?.[jobId]?.integrations) ?? []
  const activeCount = usedIntegrations.length

  const dispatch = useAppDispatch()
  const addIntegrationButtonRef = useRef<HTMLInputElement>(null)
  const settingsBlockId = 'job-integrations'
  const settingsBlockTitle = activeCount
    ? `Active ${plur('Integration', activeCount)}`
    : 'No Active Integrations'

  const [selectedIntegration, setSelectedIntegration] = React.useState<IntegrationType | null>(null)

  const isIntegrationFormOpenedExternally = useAppSelector(state =>
    isPipelineFormOpened(state, IntegrationType.DOCKER, FormType.PIPELINE_INTEGRATION),
  )
  const integrationFormId = useAppSelector(state => state.pipelines.pipelineForm.id)

  React.useEffect(() => {
    if (
      isIntegrationFormOpenedExternally &&
      integrationFormId != null &&
      Object.values(IntegrationType).includes(integrationFormId as IntegrationType)
    ) {
      setSelectedIntegration(integrationFormId as IntegrationType)
    }
  }, [isIntegrationFormOpenedExternally, integrationFormId])

  return (
    <>
      <SettingsBlock
        id={settingsBlockId}
        collapsible={availableIntegrations.length > 0 || selectedIntegration != null}
        collapsedDefault
        collapsedNote={uniq(
          availableIntegrations
            .filter(({parameters}) => usedIntegrations.includes(parameters.displayName!))
            .map(({type}) => integrations[type]?.displayName)
            .filter(notNull),
        ).join(', ')}
        title={settingsBlockTitle}
        onAddClick={() => addIntegrationButtonRef?.current?.click()}
        customActions={anchor => (
          <AddIntegration
            anchor={<div ref={addIntegrationButtonRef}>{anchor}</div>}
            onSelectIntegration={type => {
              setSelectedIntegration(type)
              dispatch(
                collapsedBlocks.actions.toggle({
                  id: settingsBlockId,
                  collapsed: false,
                }),
              )
            }}
          />
        )}
      >
        {availableIntegrations.map(({type, parameters, id, isDeleted}) => {
          const {displayName} = parameters
          const integrationKey = `${displayName}_${type}`
          const isEnabled = usedIntegrations.includes(parameters.displayName!)
          const itWasEnabled = savedIntegrations.includes(parameters.displayName!)
          const isUnsaved = isEnabled !== itWasEnabled

          return (
            <Integration
              id={id!}
              type={type}
              jobId={jobId}
              key={integrationKey}
              unsaved={isUnsaved}
              isEnabled={isEnabled}
              parameters={parameters}
              isDeleted={isDeleted}
            />
          )
        })}
        {selectedIntegration && (
          <IntegrationSettingsForm
            type={selectedIntegration}
            onClose={() => {
              if (isIntegrationFormOpenedExternally) {
                dispatch(pipelineForm.actions.close())
              }
              setSelectedIntegration(null)
            }}
          />
        )}
      </SettingsBlock>
    </>
  )
}

export default React.memo(JobIntegrations)
